import { For, Match, Switch } from "solid-js";
import { Dynamic } from "solid-js/web";
import { BrandGlyph } from "~/components/marketing/BrandGlyph";
import type { Logo, LogoMark as Mark } from "~/components/marketing/content";

/** A brand mark in its own colours, at the size `class` sets. */
export const LogoMark = (props: { mark: Mark; class: string }) => (
  <Switch>
    <Match when={props.mark.kind === "mask" && props.mark}>
      {(mark) => (
        <span
          class="inline-flex text-(--brand)"
          classList={{
            "dark:text-primary-text": mark().tone === "dark",
            "text-primary-text dark:text-(--brand)": mark().tone === "light",
          }}
          style={{ "--brand": mark().color }}
        >
          <BrandGlyph src={mark().src} class={props.class} />
        </span>
      )}
    </Match>
    <Match when={props.mark.kind === "svg" && props.mark}>
      {(mark) => <Dynamic component={mark().icon} class={`shrink-0 ${props.class}`} />}
    </Match>
    <Match when={props.mark.kind === "image" && props.mark}>
      {(mark) => (
        <Switch fallback={<img src={mark().src} alt="" loading="lazy" class={`shrink-0 ${props.class}`} />}>
          <Match when={mark().ground === "tile"}>
            <img src={mark().src} alt="" loading="lazy" class={`shrink-0 rounded-[22%] ${props.class}`} />
          </Match>
          <Match when={mark().ground === "plate"}>
            <span class={`grid shrink-0 place-items-center rounded-[22%] bg-white p-[10%] ${props.class}`}>
              <img src={mark().src} alt="" loading="lazy" class="size-full object-contain" />
            </span>
          </Match>
        </Switch>
      )}
    </Match>
  </Switch>
);

export const LogoPill = (props: { logo: Logo }) => (
  <span
    class="flex items-center gap-2 rounded-full border border-(--brand)/25 bg-tile bg-linear-to-r from-(--brand)/10 to-transparent to-60% py-1.5 ps-1.5 pe-3.5 shadow-sm dark:border-(--brand)/35"
    style={{ "--brand": props.logo.mark.color }}
  >
    <span class="grid size-7 place-items-center rounded-full bg-(--brand)/15 ring-1 ring-(--brand)/20">
      <LogoMark mark={props.logo.mark} class="size-4" />
    </span>
    <span class="text-sm font-medium whitespace-nowrap">{props.logo.name}</span>
  </span>
);

/**
 * Overlapping brand circles that fan out from the middle on hover, each naming
 * itself in a tooltip. The box is sized for the fanned state and both the fan
 * and the tooltip are out of flow, so hovering never moves what's around it.
 * Decorative: callers carry the names in text.
 */
export const LogoStack = (props: { logos: Logo[] }) => (
  <span
    aria-hidden="true"
    class="group/stack relative h-7 shrink-0"
    style={{ width: `${28 + (props.logos.length - 1) * 23}px` }}
  >
    <For each={props.logos}>
      {(logo, i) => (
        <span
          class="group/logo absolute top-0 size-7 rounded-full bg-tile shadow-sm ring-2 ring-tile transition-[translate] duration-200 ease-out group-hover/stack:translate-x-(--fan) hover:z-10 hover:-translate-y-0.5 motion-reduce:transition-none"
          style={{
            left: `${((props.logos.length - 1) / 2) * 3 + i() * 20}px`,
            "--fan": `${(i() - (props.logos.length - 1) / 2) * 3}px`,
            "--brand": logo.mark.color,
          }}
        >
          <Switch
            fallback={
              <span class="grid size-full place-items-center rounded-full bg-(--brand)/12 ring-1 ring-(--brand)/25">
                <LogoMark mark={logo.mark} class="size-4" />
              </span>
            }
          >
            <Match when={logo.mark.kind === "image" && logo.mark}>
              {(mark) => (
                <img
                  src={mark().src}
                  alt=""
                  loading="lazy"
                  class="size-full rounded-full"
                  classList={{ "object-cover": mark().ground === "tile", "bg-white p-1.5": mark().ground !== "tile" }}
                />
              )}
            </Match>
          </Switch>
          <span class="pointer-events-none absolute top-full left-1/2 mt-2 -translate-x-1/2 rounded-md bg-primary-text px-2 py-1 text-xs font-medium whitespace-nowrap text-inverted-text opacity-0 shadow-lg transition-opacity group-hover/logo:opacity-100">
            {logo.name}
          </span>
        </span>
      )}
    </For>
  </span>
);
